import * as React from 'react';
import type { Maybe, Settings } from '@types/gatsby-types';
import { graphql, useStaticQuery } from 'gatsby';
import { onChange, type ClientInfo } from '../lib/clientInfo';

const query = graphql`
    query BrazeSdkQuery {
        settings {
            braze {
                apiKey
                sdkEndpoint
            }
        }
    }
`;

type BrazeSettingsQuery = {
    settings: Maybe<Pick<Settings, 'braze'>>;
};

let initialized: boolean | null = null;
let initializing: Promise<BrazeSdk | null> | null = null;
let pendingClientId: string | null;

type BrazeSdk = {
    changeUser: (userId: string) => void;
    destroy: () => void;
};

function loadBraze(
    apiKey?: string,
    sdkEndpoint?: string,
    timeoutMs = 3000,
    clientId?: string,
): Promise<BrazeSdk | null> {
    if (clientId) {
        pendingClientId = clientId;
    }

    if (!initializing) {
        initializing = new Promise<BrazeSdk | null>((resolvePromise) => {
            if (!apiKey || !sdkEndpoint) {
                initialized = false;
                resolvePromise(null);
                return;
            }

            setTimeout(async () => {
                const {
                    initialize,
                    automaticallyShowInAppMessages,
                    changeUser,
                    openSession,
                    destroy,
                } = await import('@braze/web-sdk');

                initialized = initialize(apiKey, {
                    baseUrl: sdkEndpoint,
                });

                if (initialized) {
                    automaticallyShowInAppMessages();

                    if (pendingClientId) {
                        changeUser(pendingClientId);
                        pendingClientId = null;
                    }

                    openSession();

                    resolvePromise({ changeUser, destroy });
                } else {
                    resolvePromise(null);
                }
            }, timeoutMs ?? 3000);
        });
    }

    return initializing;
}

function changeBrazeUser(
    userId: string,
    apiKey?: string,
    sdkEndpoint?: string,
): void {
    loadBraze(apiKey, sdkEndpoint, 0, userId).then((brazeSdk) => {
        if (!brazeSdk) {
            console.warn(
                'could not initialize Braze SDK: API key or SDK not configured.',
            );
        }
    });
}

function destroyBrazeSdk(): void {
    if (!initializing) {
        return;
    }

    initializing.then((sdk) => {
        if (sdk) {
            sdk.destroy();
        }

        initializing = null;
        initialized = null;
    });
}

export default function useBrazeSdk() {
    const { settings } = useStaticQuery<BrazeSettingsQuery>(query);
    const apiKey = settings?.braze?.apiKey;
    const sdkEndpoint = settings?.braze?.sdkEndpoint;

    const handleClientInfoChanged = React.useCallback(
        (newClientInfo: ClientInfo | null) => {
            setTimeout(() => {
                if (newClientInfo?.clientId) {
                    changeBrazeUser(
                        newClientInfo.clientId,
                        apiKey,
                        sdkEndpoint,
                    );
                } else {
                    destroyBrazeSdk();
                }
            }, 5000);
        },
        [apiKey, sdkEndpoint],
    );

    React.useEffect(() => {
        return onChange(handleClientInfoChanged, true);
    }, [handleClientInfoChanged]);
}
